import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalAddProspectCompany.module.sass";
import Icon from "../Icon";
import Card from "../Card";
import TooltipGlodal from "../TooltipGlodal";
import { showSuccess, showError } from "../../services/toast.service";
import { generateQrCode } from "../../services/sessions.service";

const ModalQrCode = ({ visible, onClose, session }) => {
  const modalRef = useRef(null);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const refreshIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Génère le titre de la modal basé sur les données de la session
  const getModalTitle = () => {
    if (!session) {
      return "QR Code de pointage";
    }
    return `QR Code - ${session.title}`;
  };

  // Génère un nouveau QR Code avec token jetable via l'API backend
  const generateNewQrCode = useCallback(async () => {
    if (!session?.id) return;

    setIsGenerating(true);
    try {
      const result = await generateQrCode(session.id);

      if (result.success) {
        setQrCodeUrl(result.data.qrCodeUrl);
        setTokenData(result.data);

        // Calculer le temps restant en secondes
        const timeRemaining = Math.max(0, Math.floor((result.data.expiresAt - Date.now()) / 1000));
        setTimeLeft(timeRemaining);

        console.log('QR Code généré avec succès, expire dans:', timeRemaining, 'secondes');
      } else {
        showError(result.error || 'Erreur lors de la génération du QR Code');
      }
    } catch (error) {
      console.error('Erreur lors de la génération du QR Code:', error);
      showError('Erreur lors de la génération du QR Code');
    } finally {
      setIsGenerating(false);
    }
  }, [session?.id]);

  // Démarre le compte à rebours
  const startCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Génère le QR Code quand la modal s'ouvre et configure l'auto-refresh
  useEffect(() => {
    if (visible && session?.id) {
      // Générer le premier QR Code
      generateNewQrCode();

      // Configurer l'auto-refresh toutes les 40 secondes (avant expiration à 45s)
      refreshIntervalRef.current = setInterval(() => {
        generateNewQrCode();
      }, 40 * 1000);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [visible, session?.id]);

  // Démarrer le compte à rebours quand tokenData change
  useEffect(() => {
    if (tokenData && timeLeft > 0) {
      startCountdown();
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [tokenData, timeLeft, startCountdown]);

  // Gère le défilement du corps et nettoie les intervalles
  useEffect(() => {
    const targetElement = modalRef.current || document.querySelector("#modal-qrcode");

    if (visible) {
      if (targetElement) {
        disableBodyScroll(targetElement);
      }
    } else {
      clearAllBodyScrollLocks();

      // Nettoyer tous les intervalles quand la modal se ferme
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }

      // Réinitialiser les états
      setQrCodeUrl("");
      setTokenData(null);
      setTimeLeft(0);
      setIsGenerating(false);
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [visible]);

  // Gère la touche 'Échap' pour fermer la modale
  const escFunction = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", escFunction);
    return () => {
      document.removeEventListener("keydown", escFunction);
    };
  }, [escFunction]);

  // Télécharger le QR Code
  const handleDownloadQrCode = () => {
    if (!qrCodeUrl) return;

    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `qr-code-${session?.title || 'session'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Copier le lien de pointage avec token
  const handleCopyLink = async () => {
    if (!tokenData?.attendanceUrl) return;

    try {
      await navigator.clipboard.writeText(tokenData.attendanceUrl);
      showSuccess("Lien copié dans le presse-papiers !");
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
      showError("Erreur lors de la copie du lien");
    }
  };

  // Formater le temps restant en MM:SS
  const formatTimeLeft = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Ne rend le portail que si la modale est visible
  if (!visible) {
    return null;
  }

  return createPortal(
    <>
      <div id="modal-qrcode" className={styles.modal} ref={modalRef}>
        <div className={styles.control}>
          <button className={styles.close} onClick={onClose} aria-label="Fermer la modale">
            <Icon name="close" size="20" />
          </button>
        </div>
        <div className={styles.outer}>
          <Card
            className={styles.card}
            title={getModalTitle()}
            classTitle="title-green"
          >
            <div className={styles.qrContainer}>
              {isGenerating ? (
                <div className={styles.loading}>
                  <Icon name="activity" size="32" />
                  <p>Génération du QR Code...</p>
                </div>
              ) : (
                <>
                  <div className={styles.qrCodeWrapper}>
                    {qrCodeUrl && (
                      <img
                        src={qrCodeUrl}
                        alt="QR Code de pointage"
                        className={styles.qrCodeImage}
                      />
                    )}
                  </div>

                  <div className={styles.qrInfo}>
                    {tokenData && (
                      <div className={styles.tokenInfo}>
                        <Icon name="clock" size="16" />
                        Expire dans : {formatTimeLeft(timeLeft)}
                      </div>
                    )}
                  </div>

                </>
              )}
            </div>
          </Card>
          <TooltipGlodal />
        </div>
      </div>
    </>,
    document.body
  );
};

export default ModalQrCode;